/**
 * The moonshot demo's one door (2026-09-29).
 *
 * The scripted demo (`?mode=moonshot`, `/moonshot`) rode the first download
 * every visitor pays for: its fixtures, its mock fetch and its voice chat were
 * about 11 KB brotli of main.js, for a page almost nobody opens. It now loads
 * as its own `moonshot` chunk, and only where the demo is asked for.
 *
 * - `DemoMockGate` wraps the app as `DemoMockProvider` did. Outside the demo
 *   it renders its children at once and loads nothing. In the demo it loads
 *   the provider first and draws the app inside it: the mock fetch is then in
 *   place before the app's first effects run (before, the provider installed
 *   it in its own effect, after its children's). If the demo's code cannot
 *   load, the app is drawn without it rather than not at all.
 * - `MoonshotPage` is the `/moonshot` route, through deferredRoute.
 *
 * App.tsx reaches the demo only through this file and `useMoonshotMode`
 * (tests/landing/moonshotLoadsOnDemand.test.tsx).
 */

import React, { useState } from 'react';
import { deferredRoute } from '../components/DeferredRoute';
import { useDeferredModule } from '../utils/useDeferredModule';
import { isMoonshotMode } from './useMoonshotMode';
import type * as ProviderNamespace from './DemoMockProvider';

type ProviderModule = typeof ProviderNamespace;

const loadProvider = (): Promise<ProviderModule> =>
  import(/* webpackChunkName: "moonshot" */ './DemoMockProvider');

export const MoonshotPage = deferredRoute(
  () => import(/* webpackChunkName: "moonshot" */ './MoonshotPage'),
  '/moonshot',
);

const DemoMockLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { module, error } = useDeferredModule(loadProvider, 'moonshot-provider', { start: 'mount' });
  if (error) return <>{children}</>;
  if (!module) return null;
  const Provider = module.DemoMockProvider;
  return <Provider>{children}</Provider>;
};

export const DemoMockGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Decided once, as the provider decided `enabled` once at mount.
  const [demo] = useState(isMoonshotMode);
  if (!demo) return <>{children}</>;
  return <DemoMockLoader>{children}</DemoMockLoader>;
};
